import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Loader2, Inbox, Calculator, Phone, LayoutGrid, Receipt, UserPlus, Users, KeyRound, Bell, BellOff } from "lucide-react";
import { Btn } from "@/components/Btn";
import { api } from "@/lib/api";
import { PhotoThumb } from "@/components/PhotoThumb";
import { useAuth } from "@/context/AuthContext";
import ProjectBoard from "@/pages/admin/ProjectBoard";
import { ScheduleSection, CalendarConnect } from "@/pages/admin/ScheduleSection";
import { TIERS, SURCHARGES } from "@/lib/pricing";
import { RichText } from "@/components/RichText";


function LeadsTab({ onOpen }) {
  const [leads, setLeads] = useState(null);
  useEffect(() => { api.get("/leads").then(({ data }) => setLeads(data.filter((l) => !l.stage || l.stage === "Lead Created"))).catch(() => setLeads([])); }, []);
  if (!leads) return <Loader2 className="w-6 h-6 animate-spin" />;
  if (!leads.length) return <p className="font-body text-charcoal/60">No new leads right now. Once a lead moves past Lead Created, it lives on the Board.</p>;
  return (
    <div className="grid gap-4" data-testid="leads-list">
      {leads.map((l) => (
        <div key={l.id} onClick={() => onOpen(l.id)} data-testid={`lead-card-${l.id}`}
          className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut cursor-pointer hover:shadow-brutlg hover:-translate-y-0.5 transition-all">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-heading font-bold text-xl">{l.name}</div>
              <div className="font-body text-sm text-charcoal/70 flex flex-wrap gap-x-4">
                <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-coral"><Phone className="w-3 h-3 inline" /> {l.phone}</a>
                <span>{l.email}</span>
                <span>{l.city || ""} {l.address || ""}</span>
              </div>
            </div>
            <span className="text-xs font-body font-bold bg-sunny border-2 border-charcoal rounded-full px-3 py-1">{l.status}</span>
          </div>
          {l.details && <p className="mt-3 font-body text-charcoal/80">{l.details}</p>}
              {l.heard_from && <p className="mt-2 font-body text-sm text-charcoal/70" data-testid="lead-heard-from"><b>Heard about us:</b> {l.heard_from}</p>}
          {l.photo_paths?.length > 0 && <div className="mt-3 flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>{l.photo_paths.map((p) => <PhotoThumb key={p} path={p} group={l.photo_paths} size="w-16 h-16" />)}</div>}
          <div className="mt-4"><Btn size="md" variant="secondary" onClick={(e) => { e.stopPropagation(); onOpen(l.id); }} data-testid={`open-project-${l.id}`}>Open project</Btn></div>
        </div>
      ))}
    </div>
  );
}

function QuoteBuilder({ prefill, onSaved, initialMode = "quote" }) {
  const [mode, setMode] = useState(initialMode);
  const [c, setC] = useState({ customer_name: "", customer_phone: "", customer_email: "", customer_address: "", customer_city: "", job_description: "", load_size: "", notes: "" });
  const [items, setItems] = useState([]);
  const [depInvoices, setDepInvoices] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [useOptions, setUseOptions] = useState(false);
  const [selectionMode, setSelectionMode] = useState("multi");
  const [options, setOptions] = useState([]);
  const [cashEnabled, setCashEnabled] = useState(false);
  const [cashPrice, setCashPrice] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (prefill) setC((p) => ({ ...p, customer_name: prefill.name || "", customer_phone: prefill.phone || "", customer_email: prefill.email || "", customer_address: prefill.address || "", customer_city: prefill.city || "", job_description: prefill.details || "" }));
  }, [prefill]);
  const set = (k) => (e) => setC((s) => ({ ...s, [k]: e.target.value }));
  const itemsTotal = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const total = Math.max(0, itemsTotal - (Number(discount) || 0));
  const setTier = (t) => { setItems((prev) => [{ label: t.label, amount: t.amount }, ...prev.filter((i) => !TIERS.some((x) => x.label === i.label))]); setC((s) => ({ ...s, load_size: t.label })); };
  const addItem = (it) => setItems((prev) => [...prev, { ...it }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const editAmount = (idx, v) => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, amount: v } : it));
  const addDeposit = (pct) => setDepInvoices((prev) => [...prev, { amount: Math.round((total * pct) / 100), description: pct === 100 ? "Full payment" : `${pct}% deposit` }]);
  const addCustomInv = () => setDepInvoices((prev) => [...prev, { amount: 0, description: "Invoice" }]);
  const editInv = (idx, key, v) => setDepInvoices((prev) => prev.map((x, i) => i === idx ? { ...x, [key]: v } : x));
  const removeInv = (idx) => setDepInvoices((prev) => prev.filter((_, i) => i !== idx));

  const addOption = (addon = false) => setOptions((prev) => [...prev, { id: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())), title: "", price: "", description: "", addon, cash_price: "" }]);
  const setOpt = (id, key, v) => setOptions((prev) => prev.map((o) => o.id === id ? { ...o, [key]: v } : o));
  const removeOption = (id) => setOptions((prev) => prev.filter((o) => o.id !== id));

  const save = async () => {
    const invoiceOnly = mode === "invoice";
    if (useOptions && !invoiceOnly) {
      const clean = options.filter((o) => o.title.trim()).map((o) => ({
        id: o.id, title: o.title.trim(), price: Number(o.price) || 0,
        description: o.description || "", addon: !!o.addon, cash_price: Number(o.cash_price) || 0,
      }));
      if (!clean.length) { toast.error("Add at least one option with a title!"); return; }
      setBusy(true);
      try {
        const { data } = await api.post("/quotes", { ...c, line_items: [], total: 0, options: clean, selection_mode: selectionMode, cash_enabled: cashEnabled, lead_id: prefill?.id || null });
        toast.success("Options quote saved! Find it on the Board to send.");
        onSaved(data);
        setOptions([]); setUseOptions(false); setCashEnabled(false); setCashPrice("");
        setC({ customer_name: "", customer_phone: "", customer_email: "", customer_address: "", customer_city: "", job_description: "", load_size: "", notes: "" });
      } catch (e) { toast.error("Could not save"); } finally { setBusy(false); }
      return;
    }
    if (!items.length) { toast.error("Add a load size or line item first!"); return; }
    if (invoiceOnly && !depInvoices.some((inv) => Number(inv.amount) > 0)) { toast.error("Choose a deposit or full payment first!"); return; }
    setBusy(true);
    try {
      const finalItems = (Number(discount) || 0) > 0 ? [...items, { label: "Discount", amount: -Math.abs(Number(discount)) }] : items;
      const { data } = await api.post("/quotes", { ...c, line_items: finalItems, total, cash_enabled: cashEnabled, cash_price: Number(cashPrice) || 0, lead_id: prefill?.id || null });
      for (const inv of depInvoices) {
        if (Number(inv.amount) > 0) await api.post(`/quotes/${data.id}/invoices`, { amount: Number(inv.amount), description: inv.description || "Invoice", photos: [] });
      }
      toast.success(invoiceOnly ? "Invoice created! Find it on the Board." : (depInvoices.length ? "Quote + invoices saved! Find them on the Board." : "Quote saved! Find it on the Board."));
      onSaved(data);
      setItems([]); setDepInvoices([]); setDiscount(0); setCashEnabled(false); setCashPrice("");
      setC({ customer_name: "", customer_phone: "", customer_email: "", customer_address: "", customer_city: "", job_description: "", load_size: "", notes: "" });
    } catch (e) { toast.error("Could not save"); } finally { setBusy(false); }
  };

  const input = "w-full bg-white border-2 border-charcoal rounded-xl px-4 py-3 font-body text-base focus:outline-none focus:ring-2 focus:ring-sunny";
  const invoiceOnly = mode === "invoice";
  return (
    <div data-testid="quote-builder">
      <div className="flex flex-wrap gap-2 mb-5">
        <span className="font-body font-bold text-sm text-charcoal/70 self-center mr-1">What do you want to build?</span>
        <button onClick={() => setMode("quote")} data-testid="mode-quote"
          className={`font-heading font-bold text-sm rounded-full px-4 py-2 border-2 border-charcoal transition-colors ${mode === "quote" ? "bg-coral text-cream" : "bg-white"}`}>Full quote</button>
        <button onClick={() => setMode("invoice")} data-testid="mode-invoice"
          className={`font-heading font-bold text-sm rounded-full px-4 py-2 border-2 border-charcoal transition-colors ${mode === "invoice" ? "bg-coral text-cream" : "bg-white"}`}>Invoice only</button>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut">
        <h3 className="font-heading font-bold text-xl mb-3">Customer</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={input} placeholder="Name" value={c.customer_name} onChange={set("customer_name")} data-testid="qb-name" />
          <input className={input} placeholder="Phone" value={c.customer_phone} onChange={set("customer_phone")} data-testid="qb-phone" />
          <input className={input} placeholder="Email (for sending)" value={c.customer_email} onChange={set("customer_email")} data-testid="qb-email" />
          <input className={input} placeholder="City" value={c.customer_city} onChange={set("customer_city")} />
          <input className={`${input} sm:col-span-2`} placeholder="Address" value={c.customer_address} onChange={set("customer_address")} />
          <div className="sm:col-span-2">
            <label className="font-body text-sm font-bold text-charcoal/70 mb-1 block">Job description</label>
            <RichText value={c.job_description} onChange={(html) => setC((s) => ({ ...s, job_description: html }))} placeholder="Describe the job — use the toolbar for bullets, bold, alignment…" testid="qb-jobdesc" />
          </div>
          <textarea className={`${input} sm:col-span-2`} placeholder="Private notes (not shown to customer)" value={c.notes} onChange={set("notes")} />
        </div>
      </div>
      <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut">
        <h3 className="font-heading font-bold text-xl mb-2">Pricing</h3>
        {!invoiceOnly && (
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={() => setUseOptions(false)} data-testid="pricing-fixed"
              className={`font-heading font-bold text-xs rounded-full px-4 py-2 border-2 border-charcoal transition-colors ${!useOptions ? "bg-teal text-cream" : "bg-white"}`}>Fixed price</button>
            <button onClick={() => setUseOptions(true)} data-testid="pricing-options"
              className={`font-heading font-bold text-xs rounded-full px-4 py-2 border-2 border-charcoal transition-colors ${useOptions ? "bg-teal text-cream" : "bg-white"}`}>Give customer options</button>
          </div>
        )}
        {useOptions && !invoiceOnly ? (
          <div data-testid="options-builder">
            <p className="font-body text-xs text-charcoal/50 mb-3">Build a few choices — the customer picks on their quote page. Add bullet points (one per line) to describe what's included.</p>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="font-body font-bold text-sm text-charcoal/70">Customer can</span>
              <button onClick={() => setSelectionMode("multi")} data-testid="sel-multi"
                className={`font-heading font-bold text-xs rounded-full px-3 py-1.5 border-2 border-charcoal transition-colors ${selectionMode === "multi" ? "bg-coral text-cream" : "bg-white"}`}>pick as many as they want</button>
              <button onClick={() => setSelectionMode("single")} data-testid="sel-single"
                className={`font-heading font-bold text-xs rounded-full px-3 py-1.5 border-2 border-charcoal transition-colors ${selectionMode === "single" ? "bg-coral text-cream" : "bg-white"}`}>pick just one (+ add-ons)</button>
            </div>
            <label className="flex items-center gap-2 mb-3 font-heading font-bold text-sm cursor-pointer bg-green-50 border-2 border-charcoal rounded-xl px-3 py-2">
              <input type="checkbox" checked={cashEnabled} onChange={(e) => setCashEnabled(e.target.checked)} className="w-4 h-4 accent-teal" data-testid="opt-cash-toggle" />
              Offer a cash price per option (shown to customer)
            </label>
            <div className="space-y-3">
              {options.map((o, idx) => (
                <div key={o.id} className={`border-2 border-charcoal rounded-2xl p-4 ${o.addon ? "bg-sunny/30" : "bg-cream"}`} data-testid={`option-row-${idx}`}>
                  <input className={`${input} w-full mb-2`} placeholder="Option title (e.g. Full garage cleanout)" value={o.title} onChange={(e) => setOpt(o.id, "title", e.target.value)} data-testid={`opt-title-${idx}`} />
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <div className="flex items-center gap-1"><span className="font-body font-bold">$</span><input type="number" className={`${input} w-28`} placeholder="Price" value={o.price} onChange={(e) => setOpt(o.id, "price", e.target.value)} data-testid={`opt-price-${idx}`} /></div>
                    {cashEnabled && <div className="flex items-center gap-1"><span className="font-body font-bold text-green-700 text-sm">Cash $</span><input type="number" className={`${input} w-24`} placeholder="Cash" value={o.cash_price || ""} onChange={(e) => setOpt(o.id, "cash_price", e.target.value)} data-testid={`opt-cash-${idx}`} /></div>}
                    <button onClick={() => removeOption(o.id)} className="ml-auto inline-flex items-center gap-1 text-coral font-body font-bold text-sm" data-testid={`opt-remove-${idx}`}><Trash2 className="w-4 h-4" /> Remove</button>
                  </div>
                  <RichText value={o.description} onChange={(html) => setOpt(o.id, "description", html)} placeholder="What's included — use the toolbar for bullets, bold, alignment…" testid={`opt-details-${idx}`} />
                  <label className="flex items-center gap-2 mt-2 font-body text-sm cursor-pointer">
                    <input type="checkbox" checked={o.addon} onChange={(e) => setOpt(o.id, "addon", e.target.checked)} className="w-4 h-4 accent-coral" data-testid={`opt-addon-${idx}`} />
                    Optional add-on (always a checkbox, on top of a chosen option)
                  </label>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => addOption(false)} className="text-xs font-heading font-bold bg-teal text-cream rounded-full px-4 py-2 border-2 border-charcoal" data-testid="add-option"><Plus className="w-3 h-3 inline" /> Add option</button>
              <button onClick={() => addOption(true)} className="text-xs font-heading font-bold bg-sunny rounded-full px-4 py-2 border-2 border-charcoal" data-testid="add-addon"><Plus className="w-3 h-3 inline" /> Add add-on</button>
            </div>
            <p className="font-body text-xs text-charcoal/50 mt-3">No total is set here — the customer's chosen options add up on their quote page when they sign.</p>
          </div>
        ) : (
        <>
        <p className="font-body text-xs text-charcoal/50 mb-3">$200 minimum on every job.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {TIERS.map((t) => <button key={t.label} onClick={() => setTier(t)} className="text-xs font-body font-bold bg-teal text-cream rounded-full px-3 py-1.5 border-2 border-charcoal">{t.label} ${t.amount}</button>)}
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {SURCHARGES.map((s) => <button key={s.label} onClick={() => addItem(s)} className="text-xs font-body font-bold bg-sunny rounded-full px-3 py-1.5 border-2 border-charcoal">+ {s.label}</button>)}
          <button onClick={() => addItem({ label: "Custom item", amount: 0 })} className="text-xs font-body font-bold bg-white rounded-full px-3 py-1.5 border-2 border-charcoal" data-testid="qb-add-custom"><Plus className="w-3 h-3 inline" /> Custom</button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={`${input} flex-1`} value={it.label} onChange={(e) => setItems((prev) => prev.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} />
              <div className="flex items-center gap-1"><span>$</span><input type="number" className={`${input} w-24`} value={it.amount} onChange={(e) => editAmount(i, e.target.value)} /></div>
              <button onClick={() => removeItem(i)} className="text-coral"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="font-body font-bold text-sm text-charcoal/70 mr-1">Discount</span>
          <button onClick={() => setDiscount(Math.round(itemsTotal * 0.1))} disabled={!itemsTotal} className="text-xs font-body font-bold bg-green-100 rounded-full px-3 py-1.5 border-2 border-charcoal disabled:opacity-40 hover:bg-green-200 transition-colors" data-testid="qb-discount-10">10% off</button>
          <button onClick={() => setDiscount(Math.round(itemsTotal * 0.15))} disabled={!itemsTotal} className="text-xs font-body font-bold bg-green-100 rounded-full px-3 py-1.5 border-2 border-charcoal disabled:opacity-40 hover:bg-green-200 transition-colors" data-testid="qb-discount-15">15% off</button>
          <div className="flex items-center gap-1 shrink-0"><input type="number" placeholder="0" min="0" max="100" className={`${input} !w-20 text-center`} onChange={(e) => setDiscount(Math.round(itemsTotal * (Number(e.target.value) || 0) / 100))} data-testid="qb-discount-pct" /><span className="text-charcoal/60 text-sm whitespace-nowrap">% off</span></div>
          <div className="flex items-center gap-1 basis-full sm:basis-auto sm:ml-auto"><span className="text-charcoal/60 whitespace-nowrap">Discount $</span><input type="number" className={`${input} !w-28`} value={discount} onChange={(e) => setDiscount(Number(e.target.value) || 0)} data-testid="qb-discount" /></div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t-2 border-charcoal pt-3">
          <span className="font-heading font-bold text-2xl">Total</span>
          <span className="font-heading font-bold text-2xl text-coral" data-testid="qb-total">${total}</span>
        </div>

        {!invoiceOnly && (
          <div className="mt-4 border-2 border-charcoal rounded-xl p-3 bg-green-50" data-testid="qb-cash">
            <label className="flex items-center gap-2 font-heading font-bold text-sm cursor-pointer">
              <input type="checkbox" checked={cashEnabled} onChange={(e) => setCashEnabled(e.target.checked)} className="w-4 h-4 accent-teal" data-testid="qb-cash-toggle" />
              Offer a cash price (shown to customer)
            </label>
            {cashEnabled && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="font-body text-sm text-charcoal/70">Cash price</span>
                <div className="flex items-center gap-1"><span>$</span><input type="number" className={`${input} w-28`} placeholder="e.g. 4700" value={cashPrice} onChange={(e) => setCashPrice(e.target.value)} data-testid="qb-cash-price" /></div>
                {Number(cashPrice) > 0 && total > 0 && <span className="text-xs font-body font-bold text-green-700 ml-auto">Save ${Math.max(0, total - Number(cashPrice))}</span>}
              </div>
            )}
          </div>
        )}

        <div className="mt-5 border-t-2 border-charcoal/15 pt-4" data-testid="qb-invoices">
          <h4 className="font-heading font-bold text-lg mb-1">{invoiceOnly ? "Bill customer" : <>Deposits & invoices <span className="font-body text-xs text-charcoal/50">(optional)</span></>}</h4>
          <p className="font-body text-xs text-charcoal/50 mb-3">{invoiceOnly ? "Charge a deposit or bill the full amount — skips the quote and creates the invoice." : "Attach invoices to this quote — e.g. a deposit up front."}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={() => addDeposit(25)} disabled={!total} className="text-xs font-body font-bold bg-teal text-cream rounded-full px-3 py-1.5 border-2 border-charcoal disabled:opacity-40" data-testid="qb-deposit-25">25% deposit</button>
            <button onClick={() => addDeposit(50)} disabled={!total} className="text-xs font-body font-bold bg-teal text-cream rounded-full px-3 py-1.5 border-2 border-charcoal disabled:opacity-40" data-testid="qb-deposit-50">50% deposit</button>
            <button onClick={() => addDeposit(100)} disabled={!total} className="text-xs font-body font-bold bg-coral text-cream rounded-full px-3 py-1.5 border-2 border-charcoal disabled:opacity-40" data-testid="qb-full-payment">Full payment</button>
            <button onClick={addCustomInv} className="text-xs font-body font-bold bg-white rounded-full px-3 py-1.5 border-2 border-charcoal" data-testid="qb-add-invoice"><Plus className="w-3 h-3 inline" /> Custom</button>
          </div>
          <div className="space-y-2">
            {depInvoices.map((inv, i) => (
              <div key={i} className="flex items-center gap-2">
                <input className={`${input} flex-1`} value={inv.description} onChange={(e) => editInv(i, "description", e.target.value)} placeholder="Description" />
                <div className="flex items-center gap-1"><span>$</span><input type="number" className={`${input} w-24`} value={inv.amount} onChange={(e) => editInv(i, "amount", e.target.value)} /></div>
                <button onClick={() => removeInv(i)} className="text-coral"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
        </>
        )}

        <Btn variant="primary" className="w-full mt-4" onClick={save} disabled={busy} data-testid="qb-save">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (invoiceOnly ? "Create invoice" : (useOptions ? "Save options quote" : "Save quote"))}</Btn>
      </div>
      </div>
    </div>
  );
}

function NewLead({ onDone }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", address: "", city: "", details: "" });
  const [busy, setBusy] = useState(false);
  const [project, setProject] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const reloadProject = () => project && api.get(`/quotes/${project.id}`).then(({ data }) => setProject(data)).catch(() => {});
  const create = async () => {
    if (!f.name.trim()) { toast.error("Enter the customer's name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads", f);
      const { data: proj } = await api.get(`/quotes/${data.project_id}`);
      setProject(proj);
      toast.success("Lead added to the Board! Schedule a visit below (optional).");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create lead"); } finally { setBusy(false); }
  };

  const input = "w-full bg-white border-2 border-charcoal rounded-xl px-4 py-3 font-body text-base focus:outline-none focus:ring-2 focus:ring-sunny";
  if (project) {
    return (
      <div className="grid lg:grid-cols-2 gap-6" data-testid="new-lead-scheduled">
        <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut">
          <h3 className="font-heading font-bold text-xl mb-1">{project.customer_name}</h3>
          <p className="font-body text-sm text-charcoal/60 mb-4">Lead saved to the Board under "Lead Created".</p>
          <div className="flex flex-wrap gap-2">
            <Btn variant="secondary" size="md" onClick={() => { setProject(null); setF({ name: "", phone: "", email: "", address: "", city: "", details: "" }); }} data-testid="new-lead-another">Add another lead</Btn>
            <Btn variant="primary" size="md" onClick={onDone} data-testid="new-lead-goto-board">Go to Board</Btn>
          </div>
        </div>
        <ScheduleSection project={project} reload={reloadProject} />
      </div>
    );
  }
  return (
    <div className="max-w-2xl" data-testid="new-lead-form">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-heading font-bold text-xl">New lead</h2>
        <CalendarConnect />
      </div>
      <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut grid sm:grid-cols-2 gap-3">
        <input className={input} placeholder="Name" value={f.name} onChange={set("name")} type="text" autoComplete="name" data-testid="lead-name" />
        <input className={input} placeholder="Phone" value={f.phone} onChange={set("phone")} type="tel" inputMode="tel" autoComplete="tel" data-testid="lead-phone" />
        <input className={`${input} sm:col-span-2`} placeholder="Email (optional)" value={f.email} onChange={set("email")} type="email" inputMode="email" autoComplete="email" data-testid="lead-email" />
        <input className={input} placeholder="City" value={f.city} onChange={set("city")} data-testid="lead-city" />
        <input className={input} placeholder="Address" value={f.address} onChange={set("address")} data-testid="lead-address" />
        <textarea className={`${input} sm:col-span-2 min-h-20`} placeholder="What do they need hauled? (notes)" value={f.details} onChange={set("details")} data-testid="lead-details" />
        <Btn variant="primary" className="sm:col-span-2 w-full" onClick={create} disabled={busy} data-testid="lead-create">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create lead"}</Btn>
      </div>
      <p className="font-body text-xs text-charcoal/50 mt-3">After creating, you can schedule a quote visit or a job that syncs to your Google Calendar.</p>
    </div>
  );
}

function TeamTab() {
  const { user } = useAuth();
  const input = "w-full bg-white border-2 border-charcoal rounded-xl px-4 py-3 font-body text-base focus:outline-none focus:ring-2 focus:ring-sunny";
  const [team, setTeam] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/auth/team").then(({ data }) => setTeam(data)).catch(() => setTeam([])), []);
  useEffect(() => { load(); }, [load]);
  const myEmail = (user && user.email ? user.email : "").toLowerCase();
  const isOwner = !!(team || []).find((u) => u.email === myEmail && u.is_owner);

  const add = async () => {
    if (!f.email.trim() || !f.password) { toast.error("Enter an email and a password"); return; }
    setBusy(true);
    try {
      await api.post("/auth/team", f);
      toast.success(`${f.email} can now log in to the admin`);
      setF({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add that login"); }
    setBusy(false);
  };
  const remove = async (email) => {
    if (!window.confirm(`Remove admin access for ${email}?`)) return;
    try { await api.delete(`/auth/team/${encodeURIComponent(email)}`); toast.success("Login removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove that login"); }
  };
  const toggleNotify = async (u) => {
    try {
      await api.put(`/auth/team/${encodeURIComponent(u.email)}/notify`, { notify: !u.notify });
      toast.success(!u.notify ? `${u.name || u.email} will get notification emails` : `Notifications off for ${u.name || u.email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change notifications"); }
  };
  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Fill in both password boxes"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change the password"); }
    setBusy(false);
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="team-tab">
      <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut">
        <h2 className="font-heading font-bold text-xl mb-3">Admin logins</h2>
        {!team ? <Loader2 className="w-6 h-6 animate-spin" /> : (
          <ul className="divide-y-2 divide-charcoal/10" data-testid="team-list">
            {team.map((u) => (
              <li key={u.email} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div className="min-w-0">
                  <div className="font-heading font-bold break-words">{u.name || u.email}{u.is_owner ? " (owner)" : ""}{u.email === myEmail ? " (you)" : ""}</div>
                  <div className="font-body text-sm text-charcoal/60 break-all">{u.email}</div>
                </div>
                <div className="shrink-0 flex items-center gap-3">
                {isOwner && !u.is_owner ? (
                  <button onClick={() => toggleNotify(u)} data-testid={`team-notify-${u.email}`}
                    className={`inline-flex items-center gap-1 font-heading font-bold text-xs rounded-full px-3 py-1.5 border-2 border-charcoal ${u.notify ? "bg-teal text-cream" : "bg-white"}`}>
                    {u.notify ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />} Emails {u.notify ? "on" : "off"}
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 font-body text-xs text-charcoal/60">{u.notify ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />} Emails {u.notify ? "on" : "off"}</span>
                )}
                {isOwner && !u.is_owner && (
                  <button onClick={() => remove(u.email)} className="shrink-0 inline-flex items-center gap-1 font-body text-sm text-coral" data-testid={`team-remove-${u.email}`}><Trash2 className="w-4 h-4" /> Remove</button>
                )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isOwner && (
        <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut grid sm:grid-cols-2 gap-3" data-testid="team-add-form">
          <h3 className="font-heading font-bold text-lg sm:col-span-2">Add an admin</h3>
          <input className={input} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name" />
          <input className={input} type="email" placeholder="Email" autoComplete="off" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-email" />
          <input className={`${input} sm:col-span-2`} type="password" placeholder="Temporary password (8+ characters)" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="team-password" />
          <div className="sm:col-span-2"><Btn variant="primary" size="md" onClick={add} disabled={busy} data-testid="team-add"><Plus className="w-4 h-4" /> Add admin</Btn></div>
        </div>
      )}

      <div className="bg-white border-2 border-charcoal rounded-2xl p-5 shadow-brut grid sm:grid-cols-2 gap-3" data-testid="team-password-form">
        <h3 className="font-heading font-bold text-lg sm:col-span-2">Change my password</h3>
        <input className={input} type="password" placeholder="Current password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" />
        <input className={input} type="password" placeholder="New password (8+ characters)" autoComplete="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
        <div className="sm:col-span-2"><Btn variant="secondary" size="md" onClick={changePw} disabled={busy} data-testid="pw-save"><KeyRound className="w-4 h-4" /> Save new password</Btn></div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");
  const [prefill, setPrefill] = useState(null);
  const [openLeadId, setOpenLeadId] = useState(null);

  useEffect(() => { if (user === false) nav("/admin"); }, [user, nav]);
  if (!user) return <div className="min-h-screen bg-charcoal flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cream" /></div>;

  const openLeadProject = (leadId) => { setOpenLeadId(leadId); setTab("board"); };
  const TABS = [{ id: "leads", label: "Leads", icon: Inbox }, { id: "newlead", label: "New Lead", icon: UserPlus }, { id: "board", label: "Board", icon: LayoutGrid }, { id: "new", label: "New Quote", icon: Calculator }, { id: "invoice", label: "New Invoice", icon: Receipt }, { id: "team", label: "Team", icon: Users }];

  return (
    <div className="min-h-screen bg-cream">
      <Helmet><title>Admin Dashboard</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <header className="bg-charcoal text-cream border-b-2 border-charcoal">
        <div className="mx-auto max-w-6xl px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="font-heading font-bold text-xl">Skedaddle <span className="text-coral">Admin</span></div>
          <button onClick={() => { logout(); nav("/admin"); }} className="inline-flex items-center gap-2 font-body font-bold text-sm" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); if (t.id !== "new" && t.id !== "invoice") setPrefill(null); }} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 shrink-0 whitespace-nowrap font-heading font-bold text-sm md:text-base rounded-full px-3.5 py-2 md:px-5 md:py-2.5 border-2 border-charcoal transition-colors ${tab === t.id ? "bg-coral text-cream" : "bg-white"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "leads" && <LeadsTab onOpen={openLeadProject} />}
        {tab === "newlead" && <NewLead onDone={() => setTab("board")} />}
        {tab === "board" && <ProjectBoard openLeadId={openLeadId} onOpened={() => setOpenLeadId(null)} />}
        {tab === "new" && <QuoteBuilder key="quote" prefill={prefill} initialMode="quote" onSaved={() => setTab("board")} />}
        {tab === "invoice" && <QuoteBuilder key="invoice" prefill={prefill} initialMode="invoice" onSaved={() => setTab("board")} />}
        {tab === "team" && <TeamTab />}
      </div>
    </div>
  );
}
